<script lang="ts">
  import { session } from '../../../lib/auth.svelte';
  import { tenantCtx } from '../../../lib/tenant.svelte';
  import { resolveDisplayName } from '../../../lib/branding.svelte';

  let {
    emoji = '🏥',
    title,
    subtitle = '',
    variant = 'admin',
    showPlus = false,
  }: {
    emoji?: string;
    title: string;
    subtitle?: string;
    variant?: string;
    showPlus?: boolean;
  } = $props();

  const usuario = $derived(session.value?.usuario ?? 'Usuario');
  const clinicName = $derived(
    resolveDisplayName(tenantCtx.branding, tenantCtx.nombre, session.value?.tenantId ?? ''),
  );
</script>

<div class="hero-wrap hero-{variant}">
  <div class="hero-glass">
    <span class="hero-emoji" aria-hidden="true">{emoji}</span>
    <div class="hero-body">
      <div class="hero-top">
        <h1>{title}</h1>
        {#if showPlus}
          <span class="plan-chip">Plus · todos los módulos</span>
        {/if}
      </div>
      <p>
        {subtitle || `Bienvenido, ${usuario.split('@')[0]}`}
        {#if clinicName && clinicName !== title}
          · <strong>{clinicName}</strong>
        {/if}
      </p>
    </div>
    <div class="hero-orb"></div>
    <div class="hero-orb orb-2"></div>
  </div>
</div>

<style>
  .hero-wrap {
    position: relative;
    border-radius: var(--radius-xl);
    margin-bottom: 1rem;
    overflow: hidden;
  }

  .hero-glass {
    position: relative;
    display: flex;
    gap: 1.25rem;
    align-items: center;
    padding: 1.5rem 1.75rem;
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: var(--radius-xl);
    color: #fff;
    z-index: 1;
    overflow: hidden;
  }

  /* Gradient backgrounds per role */
  .hero-wrap { background: linear-gradient(135deg, #5b21b6, #8b5cf6, #6366f1); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-admin       { background: linear-gradient(135deg, #004e64, #0096c7, #00b4d8); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-enfermeria  { background: linear-gradient(135deg, #1e3a5f, #3b82f6, #60a5fa); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-medico      { background: linear-gradient(135deg, #0f766e, #14b8a6, #2dd4bf); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-recepcion   { background: linear-gradient(135deg, #92400e, #f59e0b, #fbbf24); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-farmacia    { background: linear-gradient(135deg, #5b21b6, #8b5cf6, #a78bfa); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-laboratorio { background: linear-gradient(135deg, #3730a3, #6366f1, #818cf8); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-imagenologia{ background: linear-gradient(135deg, #0e7490, #22d3ee, #67e8f9); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-quirofano   { background: linear-gradient(135deg, #9f1239, #e11d48, #fb7185); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-caja        { background: linear-gradient(135deg, #166534, #22c55e, #4ade80); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-finanzas    { background: linear-gradient(135deg, #1e3a8a, #3b82f6, #60a5fa); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-mspas       { background: linear-gradient(135deg, #374151, #6b7280, #9ca3af); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-auditoria   { background: linear-gradient(135deg, #1f2937, #4b5563, #6b7280); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-vet         { background: linear-gradient(135deg, #14532d, #22c55e, #4ade80); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-optica      { background: linear-gradient(135deg, #312e81, #6366f1, #818cf8); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-clinic      { background: linear-gradient(135deg, #0c4a6e, #0284c7, #38bdf8); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-odontologia { background: linear-gradient(135deg, #0e7490, #06b6d4, #67e8f9); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-nutricion   { background: linear-gradient(135deg, #166534, #22c55e, #86efac); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-psicologia  { background: linear-gradient(135deg, #6b21a8, #a855f7, #c4b5fd); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-estetica    { background: linear-gradient(135deg, #9d174d, #ec4899, #f9a8d4); background-size: 200% 200%; animation: heroShift 8s ease infinite; }
  .hero-fisioterapia{ background: linear-gradient(135deg, #1d4ed8, #3b82f6, #93c5fd); background-size: 200% 200%; animation: heroShift 8s ease infinite; }

  @keyframes heroShift {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  .hero-emoji {
    font-size: 3rem;
    line-height: 1;
    position: relative;
    z-index: 2;
    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.15));
  }

  .hero-body { flex: 1; min-width: 0; position: relative; z-index: 2; }

  .hero-top {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    flex-wrap: wrap;
  }

  .hero-glass h1 {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #fff;
  }

  .hero-glass p {
    margin: 0.35rem 0 0;
    color: rgba(255,255,255,0.78);
    font-size: 0.88rem;
    max-width: 42rem;
  }

  .hero-glass strong {
    font-weight: 700;
    color: #fff;
  }

  .plan-chip {
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding: 0.2rem 0.55rem;
    border-radius: 999px;
    background: rgba(255,255,255,0.2);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid rgba(255,255,255,0.3);
    color: #fff;
    white-space: nowrap;
  }

  .hero-orb {
    position: absolute;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
    top: -40px;
    right: -30px;
  }

  .orb-2 {
    width: 120px;
    height: 120px;
    top: auto;
    bottom: -30px;
    left: 20%;
    right: auto;
    opacity: 0.6;
  }
</style>
